import { deleteCartAndRemoveKeys, productsInTheCart } from '@helpers/cartFunctions';
import toggleErrorPopup from '@helpers/toggleErrorPopup';
import { getCheckoutInfo, getProductDetails } from '@utils/services';
import sessionStorageKeys from '@constants/sessionStorageKeys';
import { useCookies } from 'react-cookie';
import User from '@customTypes/user';
import { bookNowClicked } from '@events/DLP';
import { Dispatch, SetStateAction } from 'react';
import { UserAction } from '@customTypes/context';
import { CartItemDetails } from '@customTypes/cart-items';
import { useRouter } from 'next/router';
import { useEvents } from '@events/EventsProvider';
import { SHOW_PRODUCT_NO_LONGER_AVAILABLE } from '@constants/amplitudeEvents';
import { CheckoutInfo, AmplitudeCheckoutData } from '@customTypes/checkout-info';
import { BrowserClient } from '@amplitude/analytics-types';
import useSessionStorage from './useSessionStorage';
import useAmplitudePayloads from './useAmplitudePayloads';

export default function useCheckout() {
  const router = useRouter();
  const { track } = useEvents();
  const { getItem } = useSessionStorage();
  const [cookies] = useCookies();
  const user: User = cookies.userInformation;
  const cartId = getItem(sessionStorageKeys.CART_ID);
  const { generateProductPayload } = useAmplitudePayloads();

  const getAmplitudeCheckoutData = (amplitude: BrowserClient | null): AmplitudeCheckoutData => {
    const amplitudeDeviceId = amplitude?.getDeviceId() ?? null;
    const amplitudeSessionId = amplitude?.getSessionId() ?? null;

    return {
      amplitudeDeviceId,
      amplitudeSessionId,
    };
  };

  /**
   * Track an event for products that are no longer available in the cart.
   *
   * @param {CheckoutInfo | undefined} checkoutDetails - The checkout information.
   */
  const trackProductNoLongerAvailableEvent = async (checkoutDetails: CheckoutInfo | undefined) => {
    try {
      if (checkoutDetails) {
        const products = await getProductDetails(checkoutDetails.hotel_id);
        const cartProducts = productsInTheCart(checkoutDetails, products.products);

        track(SHOW_PRODUCT_NO_LONGER_AVAILABLE, {
          products: cartProducts.map((product) => generateProductPayload(product)),
        });
      }
    } catch (error) {
      // Handle errors if necessary.
    }
  };

  const navigateToCheckoutConfirmation = async (
    dispatch: Dispatch<UserAction>,
    setCartData: Dispatch<SetStateAction<CartItemDetails | undefined>>,
    previousCheckoutDetails: CheckoutInfo | undefined,
  ) => {
    try {
      // Event
      const checkOutInfo = await getCheckoutInfo(cartId, user);
      bookNowClicked(checkOutInfo);

      router.push('/users/guest-billing-detail');
    } catch (error: any) {
      if (error) {
        const { data } = error.response;
        if (data) {
          const message = data.event;
          if (message === 'inventory_unavailable') {
            await trackProductNoLongerAvailableEvent(previousCheckoutDetails);

            deleteCartAndRemoveKeys(cartId, dispatch, user);
            setCartData(undefined);
            toggleErrorPopup(
              'One or more products from your cart are no longer available',
              dispatch,
            );

            return;
          }

          toggleErrorPopup(error, dispatch);
        }
      }
    }
  };

  return {
    getAmplitudeCheckoutData,
    navigateToCheckoutConfirmation,
  };
}
